<script>
import { getCardPaymentLimit, saveCardPaymentLimit } from '@/api/paymentLimit.js'

// 금액 칸은 화면에서 '1,000,000' 처럼 쉼표를 찍어 보여 주고 저장할 때 숫자로 되돌린다.
// 비어 있으면 null - 그 한도가 없다는 뜻이다.
const toNumber = (text) => {
  const digits = String(text ?? '').replace(/[^0-9]/g, '')
  return digits === '' ? null : Number(digits)
}
const toText = (amount) => (amount === null || amount === undefined ? '' : Number(amount).toLocaleString('ko-KR'))

export default {
  name: 'PaymentLimitPolicyPage',
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        perProductMaxAmount: '',
        perPaymentMaxAmount: '',
        dailyMaxAmount: '',
        activeYn: 'Y',
        memo: '',
      },
      updatedDt: null,
      updatedByName: '',
      updatedBy: '',
      activeOptions: [
        { title: '사용', value: 'Y' },
        { title: '미사용 (한도 없음)', value: 'N' },
      ],
    }
  },
  computed: {
    summary() {
      if (this.form.activeYn !== 'Y') return '한도를 쓰지 않습니다. 카드 결제 금액에 제한이 없습니다.'
      const parts = []
      const product = toNumber(this.form.perProductMaxAmount)
      const payment = toNumber(this.form.perPaymentMaxAmount)
      const daily = toNumber(this.form.dailyMaxAmount)
      if (product) parts.push(`1개 ${product.toLocaleString('ko-KR')}원이 넘는 상품은 카드로 살 수 없습니다`)
      if (payment) parts.push(`카드 결제 한 번에 ${payment.toLocaleString('ko-KR')}원까지`)
      if (daily) parts.push(`한 회원이 하루에 ${daily.toLocaleString('ko-KR')}원까지`)
      return parts.length ? parts.join(' · ') : '세 칸이 모두 비어 있어 제한이 없습니다.'
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        const res = await getCardPaymentLimit()
        if (res.data?.code === 0) {
          this.apply(res.data.data)
        } else {
          alert(res.data?.message || '카드 결제 한도를 불러오지 못했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '카드 결제 한도를 불러오는 중 오류가 발생했습니다.')
      } finally {
        this.loading = false
      }
    },
    apply(policy) {
      if (!policy) return
      this.form = {
        perProductMaxAmount: toText(policy.perProductMaxAmount),
        perPaymentMaxAmount: toText(policy.perPaymentMaxAmount),
        dailyMaxAmount: toText(policy.dailyMaxAmount),
        activeYn: policy.activeYn || 'Y',
        memo: policy.memo || '',
      }
      this.updatedDt = policy.updatedDt
      this.updatedByName = policy.updatedByName || ''
      this.updatedBy = policy.updatedBy || ''
    },
    formatAmountField(field) {
      this.form[field] = toText(toNumber(this.form[field]))
    },
    validate() {
      const product = toNumber(this.form.perProductMaxAmount)
      const payment = toNumber(this.form.perPaymentMaxAmount)
      const daily = toNumber(this.form.dailyMaxAmount)
      for (const [label, value] of [['상품 1개 최대 금액', product], ['1회 최대 결제금액', payment], ['1일 누적 최대 결제금액', daily]]) {
        if (value !== null && value <= 0) return `${label}은 1원 이상이어야 합니다. 한도를 두지 않으려면 비워 두세요.`
      }
      if (product && payment && product > payment) return '상품 1개 최대 금액은 1회 최대 결제금액보다 클 수 없습니다.'
      if (payment && daily && payment > daily) return '1회 최대 결제금액은 1일 누적 최대 결제금액보다 클 수 없습니다.'
      if (product && daily && product > daily) return '상품 1개 최대 금액은 1일 누적 최대 결제금액보다 클 수 없습니다.'
      return null
    },
    async save() {
      const message = this.validate()
      if (message) {
        alert(message)
        return
      }
      if (!confirm('카드 결제 한도를 저장합니다. 저장하는 즉시 자사몰 주문 · 결제에 적용됩니다.')) return
      this.saving = true
      try {
        const res = await saveCardPaymentLimit({
          perProductMaxAmount: toNumber(this.form.perProductMaxAmount),
          perPaymentMaxAmount: toNumber(this.form.perPaymentMaxAmount),
          dailyMaxAmount: toNumber(this.form.dailyMaxAmount),
          activeYn: this.form.activeYn,
          memo: this.form.memo,
        })
        if (res.data?.code === 0) {
          this.apply(res.data.data)
          alert('저장했습니다.')
        } else {
          alert(res.data?.message || '카드 결제 한도 저장에 실패했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '카드 결제 한도 저장 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },
    formatDt(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 19)
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">결제 한도 관리</div>
        <div class="text-body-2 text-grey mt-1">
          자사몰 카드(PG) 결제의 최대 금액을 정합니다. 계좌이체에는 걸리지 않습니다.
          한도를 넘는 카드 결제는 막고 계좌이체를 안내합니다.
        </div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-content-save-outline" :loading="saving" :disabled="loading" @click="save">
        저장
      </v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">카드 결제 한도</v-card-title>
      </v-card-item>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-card-text class="pt-5">
        <div class="limit-grid">
          <v-select
            v-model="form.activeYn"
            :items="activeOptions"
            label="사용 여부"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <div />
          <div />

          <v-text-field
            v-model="form.perProductMaxAmount"
            label="상품 1개 최대 금액"
            suffix="원"
            placeholder="비우면 한도 없음"
            variant="outlined"
            density="comfortable"
            :disabled="form.activeYn !== 'Y'"
            hint="상품 1개의 판매가 기준(수량을 곱하지 않음)"
            persistent-hint
            inputmode="numeric"
            @blur="formatAmountField('perProductMaxAmount')"
          />
          <v-text-field
            v-model="form.perPaymentMaxAmount"
            label="1회 최대 결제금액"
            suffix="원"
            placeholder="비우면 한도 없음"
            variant="outlined"
            density="comfortable"
            :disabled="form.activeYn !== 'Y'"
            hint="카드 결제 한 번(주문 1건)의 금액, 배송비 포함"
            persistent-hint
            inputmode="numeric"
            @blur="formatAmountField('perPaymentMaxAmount')"
          />
          <v-text-field
            v-model="form.dailyMaxAmount"
            label="1일 누적 최대 결제금액"
            suffix="원"
            placeholder="비우면 한도 없음"
            variant="outlined"
            density="comfortable"
            :disabled="form.activeYn !== 'Y'"
            hint="한 회원의 오늘 카드 결제 합계(진행 중 포함)"
            persistent-hint
            inputmode="numeric"
            @blur="formatAmountField('dailyMaxAmount')"
          />
        </div>

        <v-alert type="info" variant="tonal" density="compact" class="mt-6">
          {{ summary }}
        </v-alert>

        <v-textarea
          v-model="form.memo"
          label="메모"
          variant="outlined"
          density="comfortable"
          rows="2"
          auto-grow
          counter="1000"
          class="mt-5"
        />

        <div class="text-caption text-grey">
          마지막 수정 {{ formatDt(updatedDt) }}
          <span v-if="updatedByName || updatedBy"> · {{ updatedByName || updatedBy }}</span>
          · 상품 1개 ≤ 1회 ≤ 1일 순서여야 저장됩니다 · 바꾼 이력은 감사 로그에 남습니다
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.limit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 16px;
}
</style>
